import { useEffect, type ReactNode } from "react";
import { useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { RotateCw, Search, User, UserCheck, UserRound } from "lucide-react";
import { getFormErrorMessage } from "../../../../../lib/forms/form-errors";
import { KioskTextField } from "../../../../../components/kiosk-field";
import { KioskDateOfBirthField } from "../../../../../components/kiosk-date-of-birth-field";
import {
  KioskStatePanel,
  type KioskStateAction,
} from "../../../../../components/kiosk-state-panel";
import { useStepPrimaryAction } from "../../../context";
import type { PatientLookup } from "./patient-lookup";

/**
 * Stable `data-testid`s of one flow's lookup. Each flow keeps the ids its
 * end-to-end suites already use.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const ids: KioskPatientLookupTestIds = SCHEDULER_LOOKUP_TEST_IDS;
 * ```
 */
export interface KioskPatientLookupTestIds {
  readonly form: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly dateOfBirthPicker: string;
  readonly search: string;
  readonly confirm: string;
  readonly found: string;
  readonly ambiguous: string;
  readonly notFound: string;
  readonly error: string;
}

/**
 * Props of {@link KioskPatientLookupPanel}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Props = KioskPatientLookupPanelProps;
 * ```
 */
export interface KioskPatientLookupPanelProps {
  readonly lookup: PatientLookup;
  /** Unique DOM id prefix for the three inputs (floating labels bind by id). */
  readonly idPrefix: string;
  readonly testIds: KioskPatientLookupTestIds;
  /** Called with the found patient when the patient confirms "Ja, das bin ich". */
  readonly onConfirm: (patient: {
    readonly patientId: string;
    readonly insurance: "GKV" | "PKV" | "SELFPAY";
    readonly lastName: string;
    readonly dateOfBirth: string;
  }) => void;
  /**
   * What the flow shows when nobody matches: usually {@link KioskLookupNotFound}
   * with the flow's way forward, or a reception handoff when registering is
   * not allowed (composition instead of a flag, FC-11).
   */
  readonly notFound: ReactNode;
}

/**
 * The one patient lookup used by the scheduler and the prescription flow
 * (KO-07, FC-07, FB-04): fields in the order Vorname, Nachname, Geburtsdatum
 * (Vorname only once the search needs it, explained above the form, KO-70),
 * one wording for found / not unique / not found / error, and the forward
 * action in the footer: "Suchen" until a patient is found, then
 * "Ja, das bin ich" (KO-08, KO-76). Results are shown above the form so the
 * outcome is visible without scrolling (KO-09).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskPatientLookupPanel
 *   lookup={lookup}
 *   idPrefix="prescription-lookup"
 *   testIds={PRESCRIPTION_LOOKUP_TEST_IDS}
 *   onConfirm={({ patientId, insurance }) => send({ type: "MANUAL_PATIENT_FOUND", patientId, insuranceType: insurance === "GKV" ? "PUBLIC" : "PRIVATE" })}
 *   notFound={<KioskLookupNotFound testId="lookup-not-found" action={helpAction} />}
 * />
 * ```
 */
export function KioskPatientLookupPanel({
  lookup,
  idPrefix,
  testIds,
  onConfirm,
  notFound,
}: KioskPatientLookupPanelProps) {
  const { t } = useTranslation();
  const { form, state, firstNameRequested, markEdited } = lookup;
  const values = useStore(form.store, (formState) => formState.values);
  const isSearching = state.status === "searching";
  const canSearch =
    values.lastName.trim().length > 0 && values.dateOfBirth.trim().length > 0;
  const firstNameId = `${idPrefix}-firstname`;

  useEffect(
    function focusRequestedFirstName() {
      if (!firstNameRequested) return;
      document.getElementById(firstNameId)?.focus();
    },
    [firstNameId, firstNameRequested],
  );

  useStepPrimaryAction(
    state.status === "found"
      ? {
          label: t("self-service.identification.lookup.confirm"),
          icon: UserCheck,
          testId: testIds.confirm,
          onClick: () =>
            onConfirm({
              patientId: state.patientId,
              insurance: state.insurance,
              lastName: values.lastName.trim(),
              dateOfBirth: values.dateOfBirth,
            }),
        }
      : {
          label: t("self-service.identification.lookup.search"),
          icon: Search,
          testId: testIds.search,
          disabled: !canSearch,
          busy: isSearching,
          onClick: () => void form.handleSubmit(),
        },
  );

  const fieldError = (errors: readonly unknown[]) =>
    errors.length > 0 ? t(getFormErrorMessage(errors[0])) : undefined;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <LookupResult
        lookup={lookup}
        testIds={testIds}
        notFound={notFound}
        onRetry={() => void form.handleSubmit()}
      />
      <form
        data-testid={testIds.form}
        className="flex min-w-0 flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSearch && !isSearching) void form.handleSubmit();
        }}
      >
        {firstNameRequested ? (
          <form.Field name="firstName">
            {(field) => (
              <KioskTextField
                id={firstNameId}
                testId={testIds.firstName}
                label={t("self-service.identification.lookup.first-name")}
                icon={User}
                required
                autoComplete="given-name"
                value={field.state.value}
                onChange={(value) => {
                  field.handleChange(value);
                  markEdited();
                }}
                onBlur={field.handleBlur}
                disabled={isSearching}
                error={fieldError(field.state.meta.errors)}
              />
            )}
          </form.Field>
        ) : null}
        <form.Field name="lastName">
          {(field) => (
            <KioskTextField
              id={`${idPrefix}-lastname`}
              testId={testIds.lastName}
              label={t("self-service.identification.lookup.last-name")}
              icon={UserRound}
              required
              autoComplete="family-name"
              value={field.state.value}
              onChange={(value) => {
                field.handleChange(value);
                markEdited();
              }}
              onBlur={field.handleBlur}
              disabled={isSearching}
              error={fieldError(field.state.meta.errors)}
            />
          )}
        </form.Field>
        <form.Field name="dateOfBirth">
          {(field) => (
            <KioskDateOfBirthField
              id={`${idPrefix}-dob`}
              testId={testIds.dateOfBirth}
              pickerTestId={testIds.dateOfBirthPicker}
              label={t("self-service.identification.lookup.dob")}
              description={t("self-service.identification.lookup.dob-hint")}
              required
              value={field.state.value}
              onChange={(value) => {
                field.handleChange(value);
                markEdited();
              }}
              onBlur={field.handleBlur}
              disabled={isSearching}
              error={fieldError(field.state.meta.errors)}
            />
          )}
        </form.Field>
        {/* Enter on a touch keyboard submits the search. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </div>
  );
}

function LookupResult({
  lookup,
  testIds,
  notFound,
  onRetry,
}: {
  readonly lookup: PatientLookup;
  readonly testIds: KioskPatientLookupTestIds;
  readonly notFound: ReactNode;
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation();
  const { state, firstNameRequested } = lookup;

  if (state.status === "found") {
    return (
      <KioskStatePanel
        tone="success"
        testId={testIds.found}
        icon={<UserCheck />}
        title={t("self-service.identification.lookup.found-title")}
        body={t("self-service.identification.lookup.found-body")}
      />
    );
  }
  if (state.status === "ambiguous" && state.hint === "additional") {
    return (
      <KioskStatePanel
        tone="warning"
        testId={testIds.ambiguous}
        title={t("self-service.identification.lookup.ambiguous-title")}
        body={t("self-service.identification.lookup.ambiguous-body")}
      />
    );
  }
  if (firstNameRequested && (state.status === "ambiguous" || state.status === "idle")) {
    return (
      <KioskStatePanel
        tone="info"
        testId={testIds.ambiguous}
        icon={<User />}
        title={t("self-service.identification.lookup.first-name-needed-title")}
        body={t("self-service.identification.lookup.first-name-needed-body")}
      />
    );
  }
  if (state.status === "not_found") return notFound;
  if (state.status === "error") {
    return (
      <KioskStatePanel
        tone="error"
        testId={testIds.error}
        title={t("self-service.identification.lookup.error-title")}
        body={t("self-service.identification.lookup.error-body")}
        action={{
          label: t("common.retry"),
          icon: RotateCw,
          onClick: onRetry,
          testId: `${testIds.error}-retry-btn`,
        }}
      />
    );
  }
  return null;
}

/**
 * The shared "no matching data" panel of the kiosk lookup, with the flow's
 * way forward (register as a new patient, or ask the reception).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskLookupNotFound testId="frontdesk-scheduler-search-not-found" action={createAction} />
 * ```
 */
export function KioskLookupNotFound({
  testId,
  action,
}: {
  readonly testId: string;
  readonly action: KioskStateAction;
}) {
  const { t } = useTranslation();
  return (
    <KioskStatePanel
      tone="neutral"
      testId={testId}
      title={t("self-service.identification.lookup.not-found-title")}
      body={t("self-service.identification.lookup.not-found-body")}
      action={action}
    />
  );
}
